const { forEachElement } = require('../common/utils/dom');
const { onOnce } = require('../common/utils/domEvents');
const getAnalyticsEventCenter = require('../common/getAnalyticsEventCenter');
const { isObject } = require('../common/utils/type');
const clientSideValidation = require('./clientSideValidation');
const formValidation = require('./formValidation');
const forms = require('./forms');

const analyticsEventCenter = getAnalyticsEventCenter();

const FIELDS = {
  CHECK: 'bylData',
  EMAIL: 'hpEmailSignUp',
};

/**
 * Query for the elements that needed for the component behaviour
 * @param {Element} form - Newsletter pop up form
 * @returns {Object} - DOM nodes references
 */
function getFormData(form) {
  return {
    action: form.getAttribute('action'),
    elements: {
      checkbox: form.querySelector(`[name=${FIELDS.CHECK}`),
      email: form.querySelector(`[name=${FIELDS.EMAIL}`),
    },
  };
}

/**
 * Transform a custom error response into an standard format.
 * @param {Object} err - Error response from EMailSubscribe-Subscribe endpoint
 * @returns {Object} - Error object with an standard format for formValidation()
 */
function parseToServerErrors(err) {
  if (!isObject(err) || !err?.error) return err;

  const errors = {
    fields: {},
  };

  if (err.bothBlank) {
    errors.fields = {
      [FIELDS.CHECK]: { valid: false },
      [FIELDS.EMAIL]: { valid: false },
    };
    errors[FIELDS.CHECK] = err.msgCheck;
    errors[FIELDS.EMAIL] = err.msgEmail;
  } else if (err.typeEmail) {
    errors.fields = {
      [FIELDS.EMAIL]: { valid: false },
    };
    errors[FIELDS.EMAIL] = err.msg;
  }

  return errors;
}

/**
 * Send the request to subscribe an email to the newsletter
 * @param {string} url - URL to endpoint
 * @param {string} email - Email to subscribe
 * @returns {Promise.<Object>} - Server response
 */
function subscribe(url, email) {
  return new Promise((resolve, reject) => {
    $.ajax({
      url,
      type: 'post',
      dataType: 'json',
      data: {
        emailId: email,
        bylDataCheck: true,
      },
      success(data) {
        if (!data.error) {
          resolve(data);
        } else {
          reject(data);
        }
      },
      error(xhr, textStatus) {
        reject(xhr?.responseJSON?.message ?? textStatus);
      },
    });
  });
}

/**
 * @typedef {object} NewsletterPopUp
 * @property {Function} open - Open the modal
 */

/**
 * Factory function of the component
 * @param {Element} popUp - pop up element (ex: .newsletter-popup)
 * @returns {NewsletterPopUp} - Component instance
 */
function createNewsletterPopUp(popUp) {
  const modal = popUp.closest('.modal');
  const form = popUp.querySelector('.newsletter-popup-form');
  const formData = getFormData(form);
  const unknownError = popUp.querySelector('.form-invalid-feedback');

  function handleSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    // TODO: Fix an incorrect hiding of all error messages in checkout/formErrors.js#clearPreviousErrors
    forEachElement(
      popUp.querySelectorAll('.error-message'),
      (el) => {
        // eslint-disable-next-line no-param-reassign
        el.style.display = '';
      },
    );

    // Clear previous errors
    forms.clearFormErrors(form);
    unknownError.innerHTML = '';

    const isValid = clientSideValidation.functions.validateForm(form, event);
    if (!isValid) return;

    // eslint-disable-next-line no-param-reassign
    popUp.dataset.state = 'submitting';

    subscribe(formData.action, formData.elements.email.value)
      .then(() => {
        // eslint-disable-next-line no-param-reassign
        popUp.dataset.state = 'success';
        // Send info for Analytics
        analyticsEventCenter.publish(
          analyticsEventCenter.EVENTS.newsletterSubscribed,
          analyticsEventCenter.EVENTS.PAYLOADS.newsletterSubscribed.location.thankYouPagePopup,
        );
      }).catch((response) => {
        // eslint-disable-next-line no-param-reassign
        popUp.dataset.state = '';
        const errors = parseToServerErrors(response);
        if (isObject(response)) {
          formValidation(form, errors);
        } else {
          unknownError.innerHTML = response;
        }
      });
  }

  form.addEventListener('submit', handleSubmit);
  onOnce(window, 'beforeunload', () => {
    form.removeEventListener('submit', handleSubmit);
  });

  const emailForPopulation = formData.elements.email.dataset?.value;
  if (emailForPopulation && emailForPopulation !== 'null') {
    formData.elements.email.value = emailForPopulation;
  }

  return {
    modal,
    open() {
      $(modal).modal('show');
    },
  };
}

module.exports = () => {
  const domElement = document.querySelector('.newsletter-popup');
  if (!domElement) return null;
  return createNewsletterPopUp(domElement);
};
